import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  RENDER_MODES,
  VIOLATIONS_TAB_LIST_INITIAL_FILTERS,
} from '../../../shared/constants';
import { detailsActions, globalActions } from '../../../shared/store/actions';
import {
  type ActiveFiltersState,
  type ActiveGraphPointState,
  type AdvancedFilterState,
  type InsightItemTypes,
  type TabFilterState,
} from '../../../shared/types';
import * as apiActions from '../data-layer/violationsTabApi';
import {
  type ColumnState,
  type FilterDeltaState,
  type RenderModeState,
  type SortState,
  type ViolationsTabMitigateModalDataState,
  type ViolationsTabSidePanelGroupRowContextState,
  type ViolationsTabState,
  ViolationsTabType,
} from '../data-layer/violationsTabTypes';
import { type FilterCacheState } from '../hooks';

const initialState: ViolationsTabState = {
  isLoading: false,
  controls: {
    isInsightsOpen: true,
    renderMode: RENDER_MODES.LIST /* IS_READ_ONLY */,
    tabFilter: [] as TabFilterState[],
    filters: VIOLATIONS_TAB_LIST_INITIAL_FILTERS,
    activeFilters: [] as ActiveFiltersState[],
    filtersDelta: {} as FilterDeltaState,
    tabInAction: null,
    sortInAction: null,
    activeColumns: [] as ColumnState[],
    filterCache: {
      entries: {},
      ids: {},
      metadata: {},
    } as FilterCacheState,
    activeInsight: null,
    searchKey: null,
    activeGraphPoint: null,
  },
  list: {
    isListLoading: false,
    totalCount: null,
    rowIdToTransition: null,
  },
  bulkaction: { isFooterVisible: false, bulkCount: 0, isBulkChecked: false },
  sidepanel: {
    isSidePanelVisible: false,
    rowContext: { current: { id: '' } },
  },
  modalIsland: {
    isMitigateModalOpen: false,
    mitigateModalData: { rowId: '' },
    isRemediateModalOpen: false,
    isReviewModalOpen: false,
  },
};

const violationsTabSlice = createSlice({
  name: ViolationsTabType.SLICE_NAME,
  initialState,
  reducers: {
    // General section
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Controls section
    setIsInsightsOpen: (state, action: PayloadAction<boolean>) => {
      state.controls.isInsightsOpen = action.payload;

      if (action.payload) {
        state.sidepanel.isSidePanelVisible = false;
      }
    },
    setRenderMode: (state, action: PayloadAction<RenderModeState>) => {
      state.controls.renderMode = action.payload;

      if (state.sidepanel.isSidePanelVisible) {
        state.sidepanel.isSidePanelVisible = false;
      }

      if (action.payload === RENDER_MODES.GRAPH) {
        state.list.totalCount = null;
        state.bulkaction.isBulkChecked = false;
        state.bulkaction.isFooterVisible = false;
      }

      if (action.payload === RENDER_MODES.LIST) {
        state.list.isListLoading = true;
      }
    },
    setAdvancedFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.controls.filters = action.payload;
    },
    setTabFilters: (state, action: PayloadAction<TabFilterState[]>) => {
      state.controls.tabFilter = action.payload;
    },
    setActiveFilters: (state, action: PayloadAction<ActiveFiltersState[]>) => {
      state.controls.activeFilters = action.payload;
    },
    setFiltersDelta: (state, action: PayloadAction<FilterDeltaState>) => {
      state.controls.filtersDelta = action.payload;
    },
    setTabInAction: (state, action: PayloadAction<string | null>) => {
      state.controls.tabInAction = action.payload;

      if (state.sidepanel.isSidePanelVisible) {
        state.sidepanel.isSidePanelVisible = false;
      }
    },
    setSortInAction: (state, action: PayloadAction<SortState>) => {
      state.controls.sortInAction = action.payload;
    },
    setViewColumns: (state, action: PayloadAction<ColumnState[]>) => {
      state.controls.activeColumns = action.payload;
    },
    setFiltersCache: (state, action: PayloadAction<FilterCacheState>) => {
      state.controls.filterCache = action.payload;
    },
    setSearchKey: (state, action: PayloadAction<string | null>) => {
      state.controls.searchKey = action.payload;
    },
    setActiveInsight: (
      state,
      action: PayloadAction<InsightItemTypes | null>
    ) => {
      state.controls.activeInsight = action.payload;
    },
    setActiveGraphPoint: (
      state,
      action: PayloadAction<ActiveGraphPointState | null>
    ) => {
      state.controls.activeGraphPoint = action.payload;
    },
    // List operations
    setIsListLoading: (state, action: PayloadAction<boolean>) => {
      state.list.isListLoading = action.payload;
    },
    setListTotalCount: (state, action: PayloadAction<number | null>) => {
      state.list.totalCount = action.payload;
    },
    setListTransitionId: (
      state,
      action: PayloadAction<number | string | null>
    ) => {
      state.list.rowIdToTransition = action.payload;
    },
    // Bulk actions section
    setBulkCount: (state, action: PayloadAction<number>) => {
      state.bulkaction.bulkCount = action.payload;
    },
    setIsBulkChecked: (state, action: PayloadAction<boolean>) => {
      state.bulkaction.isBulkChecked = action.payload;
      state.bulkaction.isFooterVisible = action.payload;
      state.controls.isInsightsOpen = false;
    },
    setIsFooterVisible: (state, action: PayloadAction<boolean>) => {
      state.bulkaction.isFooterVisible = action.payload;
      state.controls.isInsightsOpen = false;
    },
    // Sidepanel section
    setIsSidePanelVisible: (state, action: PayloadAction<boolean>) => {
      state.sidepanel.isSidePanelVisible = action.payload;
      state.controls.isInsightsOpen = false;

      if (!action.payload) {
        state.sidepanel.rowContext = { current: { id: '' } };
      }
    },
    setRowContext: (
      state,
      action: PayloadAction<ViolationsTabSidePanelGroupRowContextState>
    ) => {
      state.sidepanel.rowContext = action.payload;
    },
    // Modals section
    setIsMitigateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.modalIsland.isMitigateModalOpen = action.payload;
    },
    setMitigateModalData: (
      state,
      action: PayloadAction<ViolationsTabMitigateModalDataState>
    ) => {
      state.modalIsland.mitigateModalData = action.payload;
    },
    setIsRemediateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.modalIsland.isRemediateModalOpen = action.payload;
    },
    setIsReviewModalOpen: (state, action: PayloadAction<boolean>) => {
      state.modalIsland.isReviewModalOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Add extra reducers below
    // ...
    // Matcher added to take actions when page routing happens
    builder.addMatcher(
      (action: PayloadAction<boolean>) =>
        action.type === globalActions.setViolationsActiveTab.type ||
        action.type === detailsActions.setQueryParam.type,
      (state) => {
        state.list.totalCount = null;

        if (state.sidepanel.isSidePanelVisible) {
          state.sidepanel.isSidePanelVisible = false;
        }

        if (!state.controls.isInsightsOpen) {
          state.controls.isInsightsOpen = true;
          state.controls.searchKey = null;
        }

        if (state.bulkaction.isFooterVisible) {
          state.bulkaction.isBulkChecked = false;
          state.bulkaction.isFooterVisible = false;
        }
      }
    );
  },
});

const { reducer: violationsTabReducer, actions: sliceActions } =
  violationsTabSlice;

export const violationsTabActions = {
  ...sliceActions,
  ...apiActions,
};

export default violationsTabReducer;
